import createDebug from 'debug';
import { authFetch } from './client';

const debug = createDebug('app:api:sharing');
const API_BASE = '/api';

// =============================================================================
// Types
// =============================================================================

export interface ShareInfo {
	user_id: number;
	username: string;
	display_name: string | null;
	role: 'editor' | 'viewer';
	created_at: string;
}

export interface ListSharesResponse {
	shares: ShareInfo[];
}

export interface SharedLinkInfo {
	id: number;
	url: string;
	title: string;
	description: string | null;
	queueId: number;
	metadata: Record<string, unknown> | null;
	source: string | null;
	thumbnailUrl: string | null;
	isEnriching: boolean;
	createdAt: string;
	updatedAt: string;
	archivedAt: string | null;
	tags?: {
		id: number;
		name: string;
		color: string | null;
		icon: string | null;
		isNsfw: boolean;
		createdAt: string;
		linkCount?: number;
	}[];
	// Sharing metadata
	role: 'editor' | 'viewer';
	shared_by_user_id: number;
	shared_by_username: string;
	shared_by_display_name: string | null;
	shared_at: string;
}

export interface SharedWithMeResponse {
	links: SharedLinkInfo[];
	total: number;
	limit: number;
	offset: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Share a link with another user
 * POST /api/links/:id/share
 */
export async function shareLink(
	linkId: number,
	userId: number,
	role: 'editor' | 'viewer'
): Promise<void> {
	debug('shareLink: linkId=%d userId=%d role=%s', linkId, userId, role);

	const response = await authFetch(`${API_BASE}/links/${linkId}/share`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ user_id: userId, role }),
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to share link: ${response.status}`);
	}
}

/**
 * Revoke sharing for a specific user
 * DELETE /api/links/:id/share/:userId
 */
export async function unshareLink(linkId: number, userId: number): Promise<void> {
	debug('unshareLink: linkId=%d userId=%d', linkId, userId);

	const response = await authFetch(`${API_BASE}/links/${linkId}/share/${userId}`, {
		method: 'DELETE',
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to unshare link: ${response.status}`);
	}
}

/**
 * Update share role for a specific user
 * PUT /api/links/:id/share/:userId
 */
export async function updateShareRole(
	linkId: number,
	userId: number,
	role: 'editor' | 'viewer'
): Promise<void> {
	debug('updateShareRole: linkId=%d userId=%d role=%s', linkId, userId, role);

	const response = await authFetch(`${API_BASE}/links/${linkId}/share/${userId}`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ role }),
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to update share role: ${response.status}`);
	}
}

/**
 * Get list of users a link is shared with
 * GET /api/links/:id/shares
 */
export async function getShares(linkId: number): Promise<ShareInfo[]> {
	debug('getShares: linkId=%d', linkId);

	const response = await authFetch(`${API_BASE}/links/${linkId}/shares`);

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to get shares: ${response.status}`);
	}

	const data: ListSharesResponse = await response.json();
	return data.shares;
}

/**
 * Get links shared with the current user
 * GET /api/links/shared-with-me
 */
export async function getSharedWithMe(params?: {
	limit?: number;
	offset?: number;
	role?: 'editor' | 'viewer';
	sharedBy?: number;
}): Promise<SharedWithMeResponse> {
	debug('getSharedWithMe: %o', params);

	const query = new URLSearchParams();
	if (params?.limit) query.set('limit', String(params.limit));
	if (params?.offset) query.set('offset', String(params.offset));
	if (params?.role) query.set('role', params.role);
	if (params?.sharedBy) query.set('shared_by', String(params.sharedBy));

	const url = `${API_BASE}/links/shared-with-me${query.toString() ? `?${query}` : ''}`;
	const response = await authFetch(url);

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to get shared links: ${response.status}`);
	}

	return response.json();
}
